import React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { get, post } from '../../utils/api';
import { Form, FormInput } from '../common/Form';

const ExhibitorAdd = () => {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const { toevent } = useParams();
	const lang = i18n.language;
	const auth = useSelector(state => state.auth.user);
	const user = useSelector(state => state.user);
	const [ name_ru, setNameRu ] = useState('');
	const [ name_en, setNameEn ] = useState('');
	const [ weight, setWeight ] = useState('0');
    const [ event, setEvent ] = useState('');
	const [ copy, setCopy ] = useState('');
    const [ admin, setAdmin ] = useState(user ? user._id : '');
	const [ eventList, setEventList ] = useState([]);
	const [ exhibitorList, setExhibitorList ] = useState([]);
	const [ isLoading, setIsLoading ] = useState(false);
	const [ error, setError ] = useState('');
    const [ success, setSuccess ] = useState('');
	const [ counter, setCounter ] = useState({});
	const [ authEvents, setAuthEvents ] = useState([]); // Список выставок доступных текущему пользователю кабинета
	const [ agentEvents, setAgentEvents ] = useState([]); // Список выставок доступных агенту (если user - агент)
	const [ events, setEvents ] = useState([]); // Итоговый список мероприятий для выбора

	useEffect(() => {
		if(user) setAdmin(user._id)
	}, [ user ]);

	// Если указано мероприятие в УРЛ то ставим его выбором по умолчанию в списке мероприятий
	// Реагируем не только на изменение toevent, но и на eventList т.к. последний меняется позже
	useEffect(() => {
		setEvent(toevent ?? '');
	}, [ toevent, eventList ]);

	// Запрашиваем список доступных пользователю мероприятий.
	// Для root доступны все мероприятия, для admin и agent только те, к которым он привязан.
	// ВНИМАНИЕ: Руту будут возвращены только актуальные мероприятия, а админу и агенту все, включая архивные
	useEffect(() => {
		const fetchData = async () => {
			const method = auth.role === 'root' ? '/events' : auth.role === 'admin' ? '/a2e' : '/agent2event';
			const params = auth.role === 'root' ? {} : auth.role === 'admin' ? { admin: auth._id } : { agent: auth._id };
			const result = await get(method, params);
			if(result.error) {
				setAuthEvents([]);
			} else {
				let list = [];
				const data = auth.role === 'root' ? result.data.events : auth.role === 'admin' ? result.data.a2e : result.data.agent2event;
				data.forEach(item => {
					const event = auth.role === 'root' ? item : item.event;
					// для агента запомним ограничение на число экспонентов в самом мероприятии
					if(auth.role === 'agent') event.exhibitor_limit = item.limit; else event.exhibitor_limit = undefined;
					list.push(event);
				});
				setAuthEvents(list);
			}
			setEvent('');
		}
		if(auth) fetchData();
	}, [ auth, lang ]);

	// Если добавляем участника агенту, нам необходимо прочитать список привязок агента к мероприятиям
	useEffect(() => {
		const fetchData = async (agent) => {
			if(agent === auth._id) {
				// Если агент смотрит сам себя, нет нужды запрашивать данные повторно
				setAgentEvents(authEvents);
				return;
			}
			const method = '/agent2event';
			const params = { agent };
			const result = await get(method, params );
			if(result.error) {
				setAgentEvents([]);
			} else {
				const list = result.data.agent2event.map(i => {
					i.event.exhibitor_limit = i.limit;
					return i.event;
				});
				setAgentEvents(list);
			}
		};

		if(user?.role === 'agent') fetchData(user._id);
	}, [ auth, user, authEvents ]);

	// Когда у нас есть список мероприятий доступный пользователю
	// и список мероприятий агента, мы можем сделать итоговый список
	// выставок для выбора
	useEffect(() => {
		if(user?.role !== 'agent') {
			// Если привязываемый администратор не агент, то список совпадает со списком пользователя
			setEvents(authEvents);
			return;
		}
		// Если создаем экспонента для агента, необходимо найти пересечения списоков его и пользователя
		const list = [];
		authEvents.forEach(event => {
			const agentEvent = agentEvents.find(e => e._id === event._id);
			if(agentEvent) list.push(agentEvent);
		});
		setEvents(list);
	}, [ user, authEvents, agentEvents ]);

	// Запрашиваем список экспонентов для копирования и создаем два объекта:
	// 1) Список опций для дропдауна выбора источника копирования
	// 2) Объект-счетчик числа экспонентов по мероприятиям
	useEffect(() => {
		const fetchData = async (admin) => {
			const method = '/exhibitors';
			const params = { admin };
			const result = await get(method, params);
			if(result.error) {
				setExhibitorList([]);
				setCounter({});
			} else {
				let list = [];
				let counter = {};
				const data = result.data.exhibitors;
				data.forEach(item => {
					list.push({ value: item._id, text: item.name[lang] });
					const event_id = item.event._id;
					if(typeof counter[event_id] === 'number') counter[event_id] ++; else counter[event_id] = 1;
				});
				list.sort((a, b) => a.text.localeCompare(b.text));
				setExhibitorList(list);
				setCounter(counter);
			}
			setCopy('');
		}
		if(admin) fetchData(admin);
	}, [ admin, lang ]);

	// Готовим список опций для селекта выбора мероприятия.
	// Мы не делаем этого сразу т.к.
	// у агента может быть достигнут лимит по экспонентам и в
	// этом случае опцию надо отметить как недоступную.
	useEffect(() => {
		let list = [];
		events.forEach(event => {
			let limit = event.exhibitor_limit; // это поле определено только для агента
			let disabled = undefined;
			if(typeof limit === 'number' && counter[event._id] >= limit) disabled = true;
			list.push({ value: event._id, text: event.name[lang], disabled });
		});
		list.sort((a, b) => a.text.localeCompare(b.text));
		setEventList(list);
	}, [ counter, events, lang ]);


	// Отправка данных
	const onSubmit = async () => {
        setIsLoading(true);
        setError('');
        setSuccess('');
		const parsedWeight = parseInt(weight);
        const result = await post('/exhibitors', { name_ru, name_en, event, admin, copy, weight: isNaN(parsedWeight) ? 0 : parsedWeight });
        if(result.error) {
            setError(result.error);
        } else {
            setSuccess('exhibitoradd.success');
			navigate('/cabinet/exhibitorview/' + result.data.exhibitor._id + '/action/created');
        }
        setIsLoading(false);
	};

    return (
		<div>
			<h1>{t('exhibitoradd.title')}</h1>
			{(!user || (user.role !== 'exhibitor' && user.role !== 'agent')) ?
			<div className='error'>
				{t('exhibitoradd.baduser')}
			</div> :
			
			<React.Fragment>
				<div>
					{t(`exhibitoradd.subtitle1${user.role}`)}
					<NavLink to={'/cabinet/userview/' + user._id}>{user.name}</NavLink>
					{t('exhibitoradd.subtitle2')}
				</div>
				{isLoading ? <span>{t('loader.title')}</span> :
				<Form
					success={success}
					error={error}
					submit={t('exhibitoradd.submit')}
					onSubmit={onSubmit}
				>
					<FormInput
						id='name_ru'
						type='text'
						value={name_ru}
						onChange={setNameRu}
						className='lang-ru'
						label={t('exhibitoradd.name_ru')}
						required={true}
					/>
					<FormInput
						id='name_en'
						type='text'
						value={name_en}
						onChange={setNameEn}
						className='lang-en'
						label={t('exhibitoradd.name_en')}
						required={true}
					/>
					{/* Вес для сортировки может устанавливать только администратор */}
					{(auth?.role === 'root' || auth?.role === 'admin') &&
					<FormInput
						id='weight'
						type='text'
						value={weight}
						onChange={setWeight}
						label={t('exhibitoradd.weight')}
						required={true}
					/>}
					<FormInput
						id='event'
						type='select'
						value={event}
						onChange={setEvent}
						label={t('exhibitoradd.event')}
						hint={t('exhibitoradd.event_hint')}
						required={true}
						list={eventList}
					/>
					{exhibitorList.length > 0 &&
					<FormInput
						id='copy'
						type='select'
						value={copy}
						onChange={setCopy}
						label={t('exhibitoradd.copy')}
						hint={t('exhibitoradd.exhibitor_hint')}
						list={exhibitorList}
					/>}
				</Form>}
			</React.Fragment>}
		</div>
	);
};

export default ExhibitorAdd;
